import React from 'react';

import { DateTime } from 'luxon';
import { FormattedMessage, IntlShape } from 'react-intl';

import { hockeyFormatter, statFormatter } from '@nhl/formatter-utilities';
import { ReactTableEnums } from '@nhl/nhl-breakaway-ui';
import { CellContext, Row } from '@tanstack/react-table';

import CommonMessages from '~/core/commonMessages';
import { leftPaddedColumn } from '~/core/commonTableColumns';
import { Alignments, GameLocation, PlayerStatAbbrev, PlayerStatNames } from '~/core/enums';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import {
    ASSISTS_COL,
    createPlayerCol,
    createSortablePlayerCol,
    GAMES_PLAYED_COL,
    GOALS_AGAINST_AVERAGE_COL,
    GOALS_AGAINST_COL,
    GOALS_COL,
    LOSSES_COL,
    OT_LOSSES_COL,
    PENALTY_MINS_COL,
    PIM_COL,
    PLUS_MINUS_COL,
    POINTS_COL,
    PPG_COL,
    PPP_COL,
    SAVE_PCTG_COL,
    SHG_COL,
    SHOTS_COL,
    SHP_COL,
    SHUTOUTS_COL,
    TIES_COL,
    TIME_ON_ICE_COL,
    WINS_COL,
} from '~/core/tableColumns/playerTableColumns/columns';
import { getSize } from '~/core/tableColumns/utils';
import { getEnvironmentAwareHref, parseValueFromLocalizedAttribute } from '~/core/utils';
import { addSortableHeaderTitle } from '~/views/components/reactTable/utils';

import { GameLog, SeasonStats, SeasonStatsGoalie } from '../../types';

import { CAREER } from './constants';

import { GamecenterLink, HideTextOverflowColumnStyle, TeamNameColumnStyle } from './styles';

const { statsDescriptions, tableHeaders } = CommonMessages;

const sortExcludingCareerTotalsRow = (
    a: Row<SeasonStats | SeasonStatsGoalie>,
    b: Row<SeasonStats | SeasonStatsGoalie>,
    accessor: string
) => {
    if (a.original?.season === CAREER) {
        return 0;
    } else if (b.original?.season === CAREER) {
        return 0;
    }
    const aValue = a.original?.[accessor];
    const bValue = b.original?.[accessor];

    if (typeof aValue === 'string') {
        return aValue.localeCompare(bValue);
    } else if (typeof aValue === 'object') {
        const parsedA = parseValueFromLocalizedAttribute(aValue);
        const parsedB = parseValueFromLocalizedAttribute(bValue);
        return parsedA.localeCompare(parsedB);
    } else return aValue - bValue;
};

// Column Definitions
const TEAM_COL = (isLeagueNHL: boolean, intl: IntlShape) => ({
    ...createSortablePlayerCol(intl, {
        acc: 'teamName',
        header: tableHeaders.long.team,
        size: isLeagueNHL ? 80 : 140,
        alignHeader: Alignments.left,
        meta: {
            ...addSortableHeaderTitle(tableHeaders.long.team, intl),
            scope: ReactTableEnums.Scopes.ROW,
            style: TeamNameColumnStyle,
            className: 'left-aligned',
        },
    }),
    maxSize: getSize(intl, tableHeaders.long.team, isLeagueNHL ? 140 : 220),
    cell: ({ row }) => parseValueFromLocalizedAttribute(row.original.teamName),
});

const LEAGUE_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'leagueAbbrev',
        header: tableHeaders.long.league,
        size: 110,
        alignHeader: Alignments.left,
        meta: {
            ...addSortableHeaderTitle(tableHeaders.long.league, intl),
            style: {
                ...HideTextOverflowColumnStyle,
                paddingRight: '20px',
            },
            className: 'left-aligned',
        },
    });

const SEASON_COL = (intl: IntlShape) => ({
    ...createSortablePlayerCol(intl, {
        acc: 'season',
        header: tableHeaders.long.season,
        size: 100,
        alignHeader: Alignments.left,
        meta: {
            ...addSortableHeaderTitle(tableHeaders.long.season, intl),
            ...leftPaddedColumn(),
            scope: ReactTableEnums.Scopes.ROW,
            style: { ...leftPaddedColumn().style, fontWeight: 'initial' },
        },
    }),
    cell: (cell: CellContext<SeasonStats | SeasonStatsGoalie, string>) =>
        cell?.getValue() === CAREER ? (
            <FormattedMessage {...tableHeaders.long.career} />
        ) : (
            hockeyFormatter.singleSeasonFormatter(cell?.getValue())
        ),
    maxSize: getSize(intl, tableHeaders.long.season, 150),
});

const GAME_DATE_COL = (intl: IntlShape) => ({
    ...createPlayerCol(intl, {
        acc: 'gameDate',
        header: tableHeaders.long.date,
        size: 90,
        meta: {
            scope: ReactTableEnums.Scopes.ROW,
            style: { fontWeight: 'initial' },
        },
    }),
    // TODO: not sure why I can't do this on cell without an error
    accessorFn: (gameLog: GameLog) =>
        DateTime.fromISO(gameLog.gameDate).toLocaleString({
            month: 'short',
            day: 'numeric',
            year: '2-digit',
        }),
    maxSize: getSize(intl, tableHeaders.long.date, 100),
});

const TOI_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'toi',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.timeOnIce],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.timeOnIce], intl),
    });

const OPPOSITION_COL = (intl: IntlShape) => ({
    ...createPlayerCol(intl, { acc: 'opponentAbbrev', header: tableHeaders.short.opponent, size: 80 }),
    cell: function CellComponent(cell: CellContext<GameLog, string>) {
        const original = cell.row.original;
        const isWebview = useIsWebview();

        const text =
            original.homeRoadFlag === GameLocation.home ? (
                <FormattedMessage
                    defaultMessage="vs {opponentAbbrev}"
                    description="A message describing a matchup played at home, where 'vs' means 'versus'"
                    values={{ opponentAbbrev: original.opponentAbbrev }}
                />
            ) : (
                <FormattedMessage
                    defaultMessage="@ {opponentAbbrev}"
                    description="A message describing a matchup played on the road, where '@' means 'at'"
                    values={{ opponentAbbrev: original.opponentAbbrev }}
                />
            );

        return (
            <GamecenterLink href={getEnvironmentAwareHref(`/gamecenter/${original.gameId}`, isWebview)}>
                {text}
            </GamecenterLink>
        );
    },
});

const SHOTS_AGAINST_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'shotsAgainst',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.shotsAgainst],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.shotsAgainst], intl),
    });

const GAMES_STARTED_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'gamesStarted',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.gamesStarted],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.gamesStarted], intl),
    });

const SHIFTS_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'shifts',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.shifts],
        size: 40,
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.shifts], intl),
    });

const GOALIE_DECISION_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'decision',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.decision],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.decision], intl),
    });

// Column Groupings
const sharedBasicColumnsForSkaters = (intl: IntlShape) => [
    GOALS_COL(intl),
    ASSISTS_COL(intl),
    POINTS_COL(intl),
    PLUS_MINUS_COL(intl),
    PIM_COL(intl),
    PPG_COL(intl),
];

const dateAndOppositionColumns = (intl: IntlShape) => [GAME_DATE_COL(intl), OPPOSITION_COL(intl)];

const teamAndOppositionColumns = (intl: IntlShape) => [
    GAME_DATE_COL(intl),
    createPlayerCol(intl, { acc: 'teamAbbrev', header: tableHeaders.long.team, size: 40 }),
    OPPOSITION_COL(intl),
];

const sharedGameLogColumnsForGoalies = (intl: IntlShape) => [
    GAMES_STARTED_COL(intl),
    GOALIE_DECISION_COL(intl),
    SHOTS_AGAINST_COL(intl),
    GOALS_AGAINST_COL(intl),
    SAVE_PCTG_COL(intl),
];

const sharedCareerAndLogsColumnsForSkaters = (intl: IntlShape) => [PPP_COL(intl), SHG_COL(intl), SHP_COL(intl)];

const sharedCareerStatsColumns = (isLeagueNHL: boolean, intl: IntlShape) =>
    isLeagueNHL
        ? [SEASON_COL(intl), TEAM_COL(isLeagueNHL, intl), GAMES_PLAYED_COL(intl)]
        : [SEASON_COL(intl), LEAGUE_COL(intl), TEAM_COL(isLeagueNHL, intl), GAMES_PLAYED_COL(intl)];

// exported skater groupings
export const careerStatsColumnsForSkaters = (isLeagueNHL: boolean, intl: IntlShape) =>
    [
        ...sharedCareerStatsColumns(isLeagueNHL, intl),
        ...sharedBasicColumnsForSkaters(intl),
        ...sharedCareerAndLogsColumnsForSkaters(intl),
        createSortablePlayerCol(intl, {
            acc: 'avgToi',
            header: statsDescriptions.abbreviations[PlayerStatAbbrev.avgTimeOnIce],
            size: 80,
            meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.avgTimeOnIce], intl),
        }),
        createSortablePlayerCol(intl, {
            acc: 'gameWinningGoals',
            header: statsDescriptions.abbreviations[PlayerStatAbbrev.gameWinningGoals],
            size: 60,
            meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.gameWinningGoals], intl),
        }),
        createSortablePlayerCol(intl, {
            acc: 'otGoals',
            header: statsDescriptions.abbreviations[PlayerStatAbbrev.overtimeGoals],
            meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.overtimeGoals], intl),
        }),
        SHOTS_COL(intl),
        {
            ...createSortablePlayerCol(intl, {
                acc: 'shootingPctg',
                header: statsDescriptions.abbreviations[PlayerStatAbbrev.shootingPctg],
                meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.shootingPctg], intl),
            }),
            cell: (cell) => statFormatter.displayValueAs('shootingPctg')(cell?.getValue()),
            size: 75,
        },
        {
            ...createSortablePlayerCol(intl, {
                acc: 'faceoffWinningPctg',
                header: statsDescriptions.abbreviations[PlayerStatAbbrev.faceoffPctg],
                meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.faceoffPctg], intl),
            }),
            cell: (cell) => statFormatter.displayValueAs('faceoffWinPctg')(cell?.getValue()),
            size: 75,
        },
    ].map((col) => ({
        ...col,
        sortingFn: (a, b, acc) => sortExcludingCareerTotalsRow(a, b, acc),
    }));

export const gameLogsColumnsForSkaters = (intl: IntlShape) => [
    ...teamAndOppositionColumns(intl),
    ...sharedBasicColumnsForSkaters(intl),
    ...sharedCareerAndLogsColumnsForSkaters(intl),
    createSortablePlayerCol(intl, {
        acc: 'gameWinningGoals',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.gameWinningGoals],
        size: 40,
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.gameWinningGoals], intl),
    }),
    createSortablePlayerCol(intl, {
        acc: 'otGoals',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.overtimeGoals],
        size: 40,
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.overtimeGoals], intl),
    }),
    SHOTS_COL(intl, 40),
    SHIFTS_COL(intl),
    TOI_COL(intl),
];

export const lastFiveGamesColumnsForSkaters = (intl: IntlShape) => [
    ...dateAndOppositionColumns(intl),
    ...sharedBasicColumnsForSkaters(intl),
    SHG_COL(intl),
    SHOTS_COL(intl, 40),
    SHIFTS_COL(intl),
    TOI_COL(intl),
];

// exported goalie groupings
export const careerStatsColumnsForGoalies = (isLeagueNHL: boolean, intl: IntlShape) =>
    [
        ...sharedCareerStatsColumns(isLeagueNHL, intl),
        GAMES_STARTED_COL(intl),
        WINS_COL(intl),
        LOSSES_COL(intl),
        TIES_COL(intl),
        OT_LOSSES_COL(intl),
        SHOTS_AGAINST_COL(intl),
        GOALS_AGAINST_AVERAGE_COL(intl),
        SAVE_PCTG_COL(intl),
        SHUTOUTS_COL(intl),
        GOALS_COL(intl),
        ASSISTS_COL(intl),
        PIM_COL(intl),
        TIME_ON_ICE_COL(intl),
    ].map((col) => ({
        ...col,
        sortingFn: (a, b, acc) => sortExcludingCareerTotalsRow(a, b, acc),
    }));

export const gameLogsColumnsForGoalies = (intl: IntlShape) => [
    ...teamAndOppositionColumns(intl),
    ...sharedGameLogColumnsForGoalies(intl),
    SHUTOUTS_COL(intl),
    GOALS_COL(intl),
    ASSISTS_COL(intl),
    PIM_COL(intl),
    TOI_COL(intl),
];

export const lastFiveGamesColumnsForGoalies = (intl: IntlShape) => [
    ...dateAndOppositionColumns(intl),
    ...sharedGameLogColumnsForGoalies(intl),
    PENALTY_MINS_COL(intl),
];

// TODO: revisit awards columns later - ideally each award will show a set of relevant columns
const sharedAwardsColumns = (intl: IntlShape) => [
    {
        ...createPlayerCol(intl, {
            acc: 'seasonId',
            header: tableHeaders.long.season,
            meta: { headerTitle: intl.formatMessage(tableHeaders.long.season), scope: ReactTableEnums.Scopes.ROW },
        }),
        cell: (cell) => hockeyFormatter.singleSeasonFormatter(cell?.getValue()),
    },
    createPlayerCol(intl, {
        acc: 'gamesPlayed',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.gamesPlayed],
        meta: { headerTitle: intl.formatMessage(statsDescriptions.long[PlayerStatNames.gamesPlayed]) },
    }),
];

export const awardsColumnsSkater = (intl: IntlShape) => [
    ...sharedAwardsColumns(intl),
    // GOALS_COL,
    // ASSISTS_COL,
    // POINTS_COL,
    // PLUS_MINUS_COL,
    // PIM_COL,
    // {
    //     ...createPlayerCol('hits', statsDescriptions.abbreviations[PlayerStatAbbrev.hits]),
    // },
    // {
    //     ...createPlayerCol('blockedShots', statsDescriptions.abbreviations[PlayerStatAbbrev.shotsBlocked]),
    // },
];

export const awardsColumnsGoalie = (intl: IntlShape) => [
    ...sharedAwardsColumns(intl),
    // WINS_COL,
    // LOSSES_COL,
    // SAVE_PCTG_COL,
    // GOALS_AGAINST_COL,
    // //TODO: this should be a common column
    // {
    //     ...createStatsCol('gaa', statsDescriptions.abbreviations[PlayerStatAbbrev.goalsAgainstAvg]),
    //     cell: (cell) => statFormatter.displayValueAs('goalsAgainstAverage')(cell?.getValue()),
    // },
];

// const rightSidePad = {
//     id: 'rightSidePad',
//     className: 'right-side-pad',
//     // headerClassName: 'right-side-pad',
//     size: 40,
//     maxSize: 40,
// };
